import { t as normalizeParams } from "./executor-DSji1iQ0.js";
import { t as createExecutorServer } from "./executor-server-CzLw7bBK.js";
//#region src/port-transport.ts
const DEFAULT_TIMEOUT_MS = 3e4;
/** Correlates responses to requests by id and fails everything when the other side dies */
function createRequestChannel(port, timeoutMs) {
	const pending = /* @__PURE__ */ new Map();
	let nextId = 1;
	let fatalError = null;
	port.addEventListener("message", (event) => {
		const entry = pending.get(event.data?.id);
		if (!entry) return;
		pending.delete(event.data.id);
		clearTimeout(entry.timer);
		if (event.data.ok) entry.resolve(event.data);
		else entry.reject(new Error(event.data.error));
	});
	port.addEventListener?.("error", (event) => {
		fatalError = /* @__PURE__ */ new Error(`local-db: worker failed: ${event.message ?? "unknown error"}`);
		for (const [id, entry] of pending) {
			clearTimeout(entry.timer);
			pending.delete(id);
			entry.reject(fatalError);
		}
	});
	port.start?.();
	return {
		send(message) {
			if (fatalError) return Promise.reject(fatalError);
			const id = nextId++;
			return new Promise((resolve, reject) => {
				const timer = setTimeout(() => {
					pending.delete(id);
					reject(/* @__PURE__ */ new Error(`local-db: request ${id} timed out after ${timeoutMs}ms`));
				}, timeoutMs);
				pending.set(id, {
					resolve,
					reject,
					timer
				});
				port.postMessage({
					id,
					...message
				});
			});
		},
		post(message) {
			port.postMessage({
				id: nextId++,
				...message
			});
		}
	};
}
function createPortExecutor(port, options = {}) {
	const channel = createRequestChannel(port, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
	return {
		async execute(request) {
			return (await channel.send({
				kind: "execute",
				request
			})).result ?? { rows: [] };
		},
		async reset() {
			await channel.send({ kind: "reset" });
		},
		async close() {
			channel.post({ kind: "close" });
		},
		async exportFile() {
			const { bytes } = await channel.send({ kind: "export" });
			if (!bytes) throw new Error("local-db: export returned no bytes");
			return bytes;
		}
	};
}
async function handlePortRequest(server, clientId, message) {
	try {
		if (message.kind === "execute") {
			const result = await server.handle(clientId, message.request);
			return {
				id: message.id,
				ok: true,
				result
			};
		}
		if (message.kind === "export") return {
			id: message.id,
			ok: true,
			bytes: await server.exportFile()
		};
		if (message.kind === "reset") await server.reset();
		else await server.releaseClient(clientId);
		return {
			id: message.id,
			ok: true
		};
	} catch (error) {
		return {
			id: message.id,
			ok: false,
			error: error instanceof Error ? error.message : String(error)
		};
	}
}
function servePort(port, server, clientId) {
	const onMessage = async (event) => {
		const message = event.data;
		if (!message || typeof message.id !== "number") return;
		port.postMessage(await handlePortRequest(server, clientId, message));
	};
	port.addEventListener("message", onMessage);
	port.start?.();
	return () => {
		port.removeEventListener("message", onMessage);
		server.releaseClient(clientId);
	};
}
//#endregion
export { createExecutorServer, createPortExecutor, handlePortRequest, normalizeParams, servePort };

//# sourceMappingURL=executor-entry.js.map